// menuConfig.js
// Pure functions that build Ant Design menu item arrays.
// No React state, no axios calls — just item construction.

import { Link } from 'react-router-dom';
import {
   ProjectOutlined,
   CloudOutlined,
   FileDoneOutlined,
   LogoutOutlined,
   EditOutlined,
   FileOutlined,
   TeamOutlined,
   HomeOutlined,
   ScheduleOutlined,
   CalendarOutlined,
   ProfileOutlined,
   CarryOutOutlined,
   ReadOutlined,
   BarChartOutlined,
   BookOutlined,
   NotificationOutlined,
   ControlOutlined,
   SolutionOutlined,
   ToTopOutlined,
   ClusterOutlined,
   UnorderedListOutlined,
   OrderedListOutlined,
   FormOutlined,
   IdcardOutlined,
   AccountBookOutlined,
   HistoryOutlined,
   AreaChartOutlined,
   DatabaseOutlined,
   VideoCameraOutlined,
   SettingOutlined,
   CloudUploadOutlined,
} from '@ant-design/icons';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';
import { isTvetTenant } from '../../misc/TvetTenant';
// The TVET wording lives in one place (TenantLabels.js). Reusing it here instead of typing the
// words again keeps the terminology gate in TenantLabels.test.js meaningful.
import { TVET_LABELS } from '../../misc/TenantLabels';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Wraps Ant Design's getItem signature */
export function getItem(label, key, icon, children) {
   return { key, icon, children, label };
}

function link(to, label, onClick) {
   return (
      <Link to={to} onClick={onClick}>
         <span>{label}</span>
      </Link>
   );
}

function clearSelectedTeacher() {
   localStorage.removeItem('selected-teacher');
   localStorage.removeItem('selected-teacher-name');
}

/**
 * TVET parity: Registrar / App-Admin accounts, plus any account tagged with
 * department = -100 at login, may open the batch-based applicant list.
 * 1TVET keeps this in Utils.hasOldApplicantAccess; it lives here so CC's
 * shared Utils stays untouched.
 */
export function hasOldApplicantAccess(userRole) {
   if (userRole === 'Registrar' || userRole === 'App-Admin') return true;

   try {
      const otherData = JSON.parse(
         Utils.getCurrentUserInfo(Enums.UserInfo.otherdata) ?? '{}'
      );
      return Number(otherData.Department) === -100;
   } catch (error) {
      return false;
   }
}

/** `/applicantsbybatch` — the MER-side old applicant list (see App.js). */
function oldApplicantsItem() {
   return getItem(
      link('/applicantsbybatch', 'Old Student Applicants'),
      '1.1',
      <HistoryOutlined />
   );
}

function microcredentialsItem({
   key,
   lessonTo = '#',
   lessonOnClick,
   homeworkTo = '#',
   homeworkOnClick,
   performanceTaskTo = '/microcredentials/performance-tasks',
   performanceTaskOnClick,
   writtenWorksTo = '/microcredentials/written-works',
   writtenWorksOnClick,
   assessmentTo,
   assessmentOnClick,
   managementTo,
   managementOnClick,
   teacherGuideTo = '#',
   teacherGuideOnClick,
   showTeacherGuide = true,
}) {
   const children = [];

   if (showTeacherGuide) {
      children.push(
         getItem(
            link(teacherGuideTo, "Teacher's Guide", teacherGuideOnClick),
            `${key}-teacher-guide`,
            <ReadOutlined />
         )
      );
   }

   children.push(
      getItem(
         link(performanceTaskTo, 'Performance Task', performanceTaskOnClick),
         `${key}-performance-task`,
         <BookOutlined />
      ),
      getItem(
         link(writtenWorksTo, 'Written Works', writtenWorksOnClick),
         `${key}-written-works`,
         <BookOutlined />
      )
   );

   if (assessmentTo) {
      children.push(
         getItem(
            link(assessmentTo, 'Assessment', assessmentOnClick),
            `${key}-assessment`,
            <FileDoneOutlined />
         )
      );
   }

   if (managementTo) {
      children.push(
         getItem(
            link(managementTo, 'Course & Topic Management', managementOnClick),
            `${key}-management`,
            <SettingOutlined />
         )
      );
   }

   return getItem('Brainee Microcredentials', key, <ReadOutlined />, children);
}

// ---------------------------------------------------------------------------
// Shared / reusable items
// ---------------------------------------------------------------------------

export const logoutItem = (onConfirm) =>
   getItem(link('#', 'Logout', onConfirm), 'logout', <LogoutOutlined />);

export const homeItem = getItem(link('/home', 'Home'), '1', <HomeOutlined />);

// export const homeItem = getItem("Home", "sub-1", <HomeOutlined />, [
//   getItem(link("/home", "Home"), "2", <ScheduleOutlined />),
//   getItem(link("/announcement", "School Feed"), "3", <CalendarOutlined />),
// ]);

export const scheduleMenu = getItem('Schedule', 'sub0', <ScheduleOutlined />, [
   getItem(link('/classsched', 'Class Schedule'), '2', <ScheduleOutlined />),
   getItem(link('/lessoncalendar', 'Lesson Calendar'), '3',<CalendarOutlined />),
   getItem(link('/schoolcalendar', 'School Calendar'), '4', <CalendarOutlined />),
]);

export const reportCardMenu = getItem(
   'Report Card & Certificates',
   'sub1',
   <ProfileOutlined />,
   [
      getItem(link('/reportcards', 'Report Card'), '5', <ProfileOutlined />),
      getItem(link('/certificates', 'Certificate'), '6', <ProfileOutlined />),
   ]
);

export const attendanceMenu = getItem(
   'Attendance',
   'sub2',
   <CarryOutOutlined />,
   [
      getItem(
         link('/classroomattendance', 'Classroom'),
         '7',
         <ProfileOutlined />
      ),
      getItem(link('/periodattendance', 'Period'), '8', <ProfileOutlined />),
      getItem(link('/attendance', 'Gate'), '9', <ProfileOutlined />),
      getItem(
         'Report',
         'g0',
         <BarChartOutlined />,
         [
            getItem(
               link('classroomattendancerpt', 'Classroom Attendance'),
               '48'
            ),
            getItem(link('periodattendancerpt', 'Period Attendance'), '49'),
         ],
         'group'
      ),
   ]
);

export const managementMenuAdmin = getItem(
   'Management',
   'sub5',
   <ControlOutlined />,
   [
      getItem(
         link('/managestudentinfo', 'Student Information'),
         '31',
         <SolutionOutlined />
      ),
      getItem(
         link('/promotestudents', 'Promote Students'),
         '32',
         <ToTopOutlined />
      ),
      getItem(link('/subjects', 'Subjects'), '33', <EditOutlined />),
      getItem(
         link('/subjectgroups', 'Subject Groups'),
         '34',
         <ClusterOutlined />
      ),
      getItem(link('/sections', 'Sections'), '35', <UnorderedListOutlined />),
      getItem(link('/levels', 'Levels'), '36', <OrderedListOutlined />),
      getItem(
         link('/managestaffinfo', 'Staff Information'),
         '37',
         <TeamOutlined />
      ),
      getItem(
         link('/appinfogroups', 'Application Form Sections'),
         '38',
         <FormOutlined />
      ),
      getItem(
         link('/appinfofields', 'Application Form Fields'),
         '39',
         <FormOutlined />
      ),
      getItem(
         link('/configsettings', 'Configuration Settings'),
         '40',
         <SettingOutlined />
      ),
   ]
);

export const studentInfoItem = getItem(
   link('/managestudentinfo', 'Student Information'),
   '31',
   <IdcardOutlined />
);

// ---------------------------------------------------------------------------
// TVET-only entries (migration plan B-4)
// ---------------------------------------------------------------------------

// The legacy 1TVET menu opened the Electude simulation tenant in a new tab — SideMenuNew.js:324
// (staff) and :390 (student). The port dropped it: before this file changed there were 0 matches
// for "Virtual Simulation" in src/, so a TVET user lost the entry entirely.
//
// The URL is the Don Bosco instance the legacy UI hardcoded. It stays a constant until D1 answers
// whether any other campus runs its own simulation tenant; if one does, this becomes a per-campus
// map the way the E-Library links below already are.
export const TVET_VIRTUAL_SIMULATION_URL = 'https://donbosco1tvet.electude.asia';

const virtualSimulationItem = (key) =>
   getItem(
      link('#', 'Virtual Simulation', () =>
         window.open(TVET_VIRTUAL_SIMULATION_URL, '_blank')
      ),
      key,
      <ProjectOutlined />
   );

// Spread into a menu array so the entry sits where the legacy menu had it and stays out of every
// CampusCloud menu: `...tvetOnly(subdomain, '13.1')`.
const tvetOnly = (subdomain, key) =>
   isTvetTenant(subdomain) ? [virtualSimulationItem(key)] : [];

// ---------------------------------------------------------------------------
// Brainee LMS sub-menus — vary by subdomain
// ---------------------------------------------------------------------------

const diagnosticGroup = (preLink, postLink) =>
   getItem(
      'Diagnostic Assessment',
      'g1',
      <CalendarOutlined />,
      [
         getItem(link(preLink, 'Pre Test'), '17'),
         getItem(link(postLink, 'Post Test'), '18'),
         getItem(link('/bdiagnostic/report', 'Reports'), '19'),
      ],
      'group'
   );

export function braineeLMSMenuForTeacher(subdomain, onUnavailable) {
   const isSbu = ['sbuibedmanila', 'sbuibedrizal'].includes(subdomain);

   if (isSbu) {
      return [
         getItem(
            link('/homeworksubjects', 'Homework & Project'),
            '13',
            <ProjectOutlined />
         ),
         getItem(
            link('/masterysubjects', 'Brainee Mastery Assessment'),
            '15',
            <FileDoneOutlined />
         ),
         getItem(
            link('/masteryassessmentreport', 'Mastery Assessment Reports'),
            '16',
            <BarChartOutlined />
         ),
         diagnosticGroup(
            '/bdiagnostic/subjects?type=pre',
            '/bdiagnostic/subjects?type=post'
         ),
      ];
   }

   return [
      getItem(
         link('/subject', 'Lesson', clearSelectedTeacher),
         '10',
         <ReadOutlined />
      ),
      getItem(
         link(
            '/formativeassessment/subjects',
            'Formative Assessment',
            clearSelectedTeacher
         ),
         '11',
         <FileDoneOutlined />
      ),
      getItem(
         link(
            '/assessment/subjects',
            'Summative Assessment',
            clearSelectedTeacher
         ),
         '12',
         <FileDoneOutlined />
      ),
      getItem(
         link('/homeworksubjects', 'Homework & Project'),
         '13',
         <ProjectOutlined />
      ),
      ...tvetOnly(subdomain, '13.1'),
      getItem(
         link('#', 'Lesson Reports', onUnavailable),
         '14',
         <BarChartOutlined />
      ),
      getItem(
         link('/masterysubjects', 'Brainee Mastery Assessment'),
         '15',
         <FileDoneOutlined />
      ),
      getItem(
         link('/masteryassessmentreport', 'Mastery Assessment Reports'),
         '16',
         <BarChartOutlined />
      ),
      diagnosticGroup(
         '/bdiagnostic/subjects?type=pre',
         '/bdiagnostic/subjects?type=post'
      ),
   ];
}

export function braineeLMSMenuForStudent(subdomain, onUnavailable) {
   const isSbu = ['sbuibedmanila', 'sbuibedrizal'].includes(subdomain);
   const o2oMode = Utils.determineOnlineOffline();

   const diagnosticStudent = getItem(
      'Diagnostic Test',
      'g1',
      <CalendarOutlined />,
      [
         getItem(
            link('/bdiagnostic/student/subjects?type=pre', 'Pre Test'),
            '13'
         ),
         getItem(
            link('/bdiagnostic/student/subjects?type=post', 'Post Test'),
            '14'
         ),
      ],
      'group'
   );

   if (isSbu) {
      return [
         getItem(
            link('/homeworksubjects', 'Homework & Project'),
            '13',
            <ProjectOutlined />
         ),
         getItem(
            link('/masterysubjects', 'Mastery Assessment'),
            '15',
            <FileDoneOutlined />
         ),
         getItem(
            link('/masteryassessmentreport', 'Mastery Assessment Reports'),
            '16',
            <BarChartOutlined />
         ),
         getItem(
            'Diagnostic Assessment',
            'g1',
            <CalendarOutlined />,
            [
               getItem(
                  link('/bdiagnostic/student/subjects?type=pre', 'Pre Test'),
                  '13'
               ),
               getItem(
                  link('/bdiagnostic/student/subjects?type=post', 'Post Test'),
                  '14'
               ),
            ],
            'group'
         ),
      ];
   }

   return [
      o2oMode === 'offline'
         ? getItem(link('#', 'Lesson', onUnavailable), '9', <ReadOutlined />)
         : getItem(link('/subject', 'Lesson'), '9', <ReadOutlined />),
      getItem(
         link('/studentformativeassessment/subjects', 'Formative Assessment'),
         '10',
         <FileDoneOutlined />
      ),
      getItem(
         link('/studentassessment/subjects', 'Summative Assessment'),
         '11',
         <FileDoneOutlined />
      ),
      getItem(
         link('/studentmasteryassessment', 'Mastery Assessment'),
         '12',
         <FileDoneOutlined />
      ),
      diagnosticStudent,
      getItem(
         link('/studenthomeworks/subjects', 'Homework & Project'),
         '15',
         <ProjectOutlined />
      ),
      ...tvetOnly(subdomain, '15.1'),
   ];
}

// ---------------------------------------------------------------------------
// E-Library helpers
// ---------------------------------------------------------------------------

const lcccBranches = [
   {
      label: 'Grade School',
      url: 'https://lcccaloocangs-library.braineeph.com',
   },
   {
      label: 'Junior High School',
      url: 'https://lcccaloocanjhs-library.braineeph.com',
   },
   {
      label: 'Senior High School',
      url: 'https://lcccaloocan-library.braineeph.com',
   },
];

export function eLibraryMenu(subdomain, onELibraryClick, startKey = 26) {
   if (subdomain === 'lcccaloocan') {
      return getItem(
         'Brainee E-Library',
         'sub3',
         <BookOutlined />,
         lcccBranches.map(({ label, url }, i) =>
            getItem(
               link('#', label, () => window.open(url, '_blank').focus()),
               String(startKey + i)
            )
         )
      );
   }
   return getItem(
      link('#', 'Brainee E-Library', onELibraryClick),
      '29',
      <BookOutlined />
   );
}

// ---------------------------------------------------------------------------
// Role-based menu builders
// ---------------------------------------------------------------------------

/**
 * Menu for a student with status '0' (enrollment-only access).
 * `subdomain` is needed for the Makati document upload entry.
 */
export function buildEnrollmentStudentMenu(onLogout, subdomain) {
   if (isTvetTenant(subdomain)) {
      return tvetEnrollmentStudentMenu(subdomain, onLogout);
   }

   return [
      subdomain === 'makati' &&
         getItem(link('/upload-docs', 'Upload'), '2', <CloudUploadOutlined />),
      getItem(
         link('/studentassessment', 'Examination'),
         '1',
         <FileDoneOutlined />
      ),
      logoutItem(onLogout),
   ].filter(Boolean);
}

/**
 * Menu for a parent with status '0'.
 */
export function buildEnrollmentParentMenu(onLogout) {
   return [
      getItem(
         link('/studentprofile', 'Update Student Information'),
         '2',
         <EditOutlined />
      ),
      getItem(
         link('/studentfees', 'Tuition Fee Assessment'),
         '3',
         <FileOutlined />
      ),
      logoutItem(onLogout),
   ];
}

/**
 * Menu for Registrar / Cashier roles.
 * Only Registrar (and department -100 accounts) get the old applicant list.
 * `subdomain` is optional so existing callers keep compiling; it selects the TVET clone.
 */
export function buildRegistrarMenu(onLogout, userRole, subdomain) {
   if (isTvetTenant(subdomain)) {
      return tvetApplicantListMenu({ userRole, subdomain, onLogout });
   }

   return [
      getItem(
         link('/applicantlist', 'Student Applicants'),
         '1',
         <TeamOutlined />
      ),
      hasOldApplicantAccess(userRole) && oldApplicantsItem(),
      logoutItem(onLogout),
   ].filter(Boolean);
}

/**
 * Menu for Guidance role.
 * `subdomain` is optional so existing callers keep compiling; it selects the TVET clone.
 */
export function buildGuidanceMenu(onLogout, subdomain) {
   if (isTvetTenant(subdomain)) {
      // Legacy Guidance sat in the applicant-list-only group (:291-295).
      return tvetApplicantListMenu({ userRole: 'Guidance', subdomain, onLogout });
   }

   return [
      getItem(
         link('/applicantlist', 'Student Applicants'),
         '1',
         <TeamOutlined />
      ),
      getItem(
         link('/assessment', 'Examination (LMS)'),
         '2',
         <FileDoneOutlined />
      ),
      logoutItem(onLogout),
   ];
}

/**
 * Menu for Student role (full access).
 */
export function buildStudentMenu({
   subdomain,
   hasOA,
   hasBraineeConference,
   hasMicrocredentials,
   hasEmergencySheet,
   onUnavailable,
   onELibraryClick,
   onLiveClassJoin,
   onLogout,
}) {
   // A TVET tenant renders the legacy 1TVET menu (see the TVET section below).
   if (isTvetTenant(subdomain)) {
      return tvetStudentMenu({
         subdomain,
         userRole: 'Student',
         hasEmergencySheet,
         hasBraineeConference,
         onLiveClassJoin,
         onLogout,
      });
   }

   const isSbu = ['sbuibedmanila', 'sbuibedrizal'].includes(subdomain);
   const o2oMode = Utils.determineOnlineOffline();
   const scheduleItems = getItem('Schedule', 'sub0', <ScheduleOutlined />, [
      getItem(link('/classsched', 'Class Schedule'), '2', <CalendarOutlined />),
      getItem(
         link('/lessoncalendar', 'Lesson Calendar'),
         '3',
         <CalendarOutlined />
      ),
      getItem(
         link('/schoolcalendar', 'School Calendar'),
         '4',
         <CalendarOutlined />
      ),
   ]);

   const reportCardItems = getItem(
      'Report Card & Certificates',
      'sub1',
      <ProfileOutlined />,
      [
         getItem(
            link('/studentreportcard', 'Report Card'),
            '5',
            <CalendarOutlined />
         ),
         getItem(
            link('/studentcertificate', 'Certificate'),
            '6',
            <CalendarOutlined />
         ),
      ]
   );

   const attendanceItems = getItem(
      'Student Attendance',
      'sub2',
      <ProfileOutlined />,
      [
         getItem(
            link('/classroomattendancerpt', 'Classroom'),
            '7',
            <CalendarOutlined />
         ),
         getItem(link('/attendance', 'Gate'), '8', <CalendarOutlined />),
      ]
   );

   const lmsItems = braineeLMSMenuForStudent(subdomain, onUnavailable);
   const microcredentials = microcredentialsItem({
      key: 'microcredentials',
      showTeacherGuide: false,
      teacherGuideOnClick: onUnavailable,
      lessonOnClick: onUnavailable,
      homeworkTo: '/studentmicrocredentials/homeworks/subjects',
      homeworkOnClick: clearSelectedTeacher,
      performanceTaskTo: '/studentmicrocredentials/performance-tasks/subjects',
      performanceTaskOnClick: clearSelectedTeacher,
      writtenWorksTo: '/studentmicrocredentials/written-works/subjects',
      writtenWorksOnClick: clearSelectedTeacher,
      assessmentTo: '/studentmicrocredsassessment/subjects',
   });
   const braineeResourcesItem = buildBraineeResourcesItem(
      subdomain,
      onUnavailable
   );
   const eLib = eLibraryMenu(subdomain, onELibraryClick, 17);

   return [
      homeItem,
      scheduleItems,
      reportCardItems,
      attendanceItems,
      getItem('Brainee LMS', 'sub3', <CloudOutlined />, lmsItems),
      ...(!isSbu && hasMicrocredentials ? [microcredentials] : []),
      hasBraineeConference &&
         getItem(
            link('#', 'Brainee Live Class', onLiveClassJoin),
            '50',
            <VideoCameraOutlined />
         ),
      braineeResourcesItem,
      eLib,
      logoutItem(onLogout),
   ];
}

/**
 * Menu for Parent role.
 */
export function buildParentMenu({
   subdomain,
   hasOA,
   hasEmergencySheet,
   onUnavailable,
   onLogout,
}) {
   // A TVET tenant renders the legacy 1TVET menu (see the TVET section below).
   if (isTvetTenant(subdomain)) {
      return tvetParentMenu({ userRole: 'Parent', onUnavailable, onLogout });
   }

   const isSbuOrMts = [
      'sbuibedmanila',
      'sbuibedrizal',
      'mts-bacoor',
      'mts-gentri',
   ].includes(subdomain);

   const studentRecordsChildren = isSbuOrMts
      ? [
           getItem(
              link('#', 'Student Information', onUnavailable),
              '12',
              <ProfileOutlined />
           ),
           getItem(
              link('#', 'Payment History', onUnavailable),
              '13',
              <HistoryOutlined />
           ),
        ]
      : [
           hasEmergencySheet &&
              getItem(
                 link('/studentemergencyinfo', 'Emergency Information'),
                 '12',
                 <ProfileOutlined />
              ),
           getItem(
              link('/soa', 'Statement of Accounts'),
              '12.1',
              <AccountBookOutlined />
           ),
           getItem(
              link('#', 'Payment History', onUnavailable),
              '13',
              <HistoryOutlined />
           ),
        ].filter(Boolean);

   return [
      homeItem,
      hasOA &&
         getItem(
            link('/admission', 'Online Enrollment'),
            '3',
            <CloudOutlined />
         ),
      getItem('Schedule', 'sub0', <ScheduleOutlined />, [
         getItem(
            link('/classsched', 'Class Schedule'),
            '4',
            <CalendarOutlined />
         ),
         getItem(
            link('/lessoncalendar', 'Lesson Calendar'),
            '5',
            <CalendarOutlined />
         ),
         getItem(
            link('/schoolcalendar', 'School Calendar'),
            '6',
            <CalendarOutlined />
         ),
      ]),
      getItem('Report Card & Certificates', 'sub1', <CalendarOutlined />, [
         getItem(
            link('/studentreportcard', 'Report Card'),
            '7',
            <CalendarOutlined />
         ),
         getItem(
            link('/studentcertificate', 'Certificate'),
            '8',
            <CalendarOutlined />
         ),
      ]),
      getItem('Student Attendance', 'sub2', <ProfileOutlined />, [
         getItem(
            link('/classroomattendancerpt', 'Classroom'),
            '9',
            <BarChartOutlined />
         ),
         getItem(
            link('/periodattendancerpt', 'Period'),
            '9.1',
            <BarChartOutlined />
         ),
         getItem(link('/attendance', 'Gate'), '10', <CalendarOutlined />),
      ]),
      getItem(
         link('/studenthomeworks/subjects', 'Homework & Project'),
         '11',
         <ProjectOutlined />
      ),
      getItem(
         'Student Records',
         'sub3',
         <AccountBookOutlined />,
         studentRecordsChildren
      ),
      logoutItem(onLogout),
   ].filter(Boolean);
}

/**
 * Menu for Teacher / Admin / Super Admin / Co-Teacher roles.
 */
export function buildStaffMenu({
   userRole,
   subdomain,
   hasZoom,
   hasBraineeConference,
   hasMicrocredentials,
   ZoomIcon,
   onUnavailable,
   onELibraryClick,
   onLiveClassPickerOpen,
   onZoomPickerOpen,
   onLogout,
}) {
   const isSbu = ['sbuibedmanila', 'sbuibedrizal'].includes(subdomain);
   const isCoTeacher = userRole === 'Co-Teacher';

   // A TVET tenant renders the legacy 1TVET menu (see the TVET section below). Co-Teacher is a
   // CampusCloud-only role the legacy UI never had, so it keeps the CampusCloud builder.
   if (isTvetTenant(subdomain) && !isCoTeacher) {
      return tvetStaffMenu({
         userRole,
         subdomain,
         hasBraineeConference,
         hasMicrocredentials,
         hasZoom,
         ZoomIcon,
         onUnavailable,
         onLiveClassPickerOpen,
         onZoomPickerOpen,
         onLogout,
      });
   }
   const isSuperAdmin = userRole === 'Super Admin';
   const isAdmin = userRole === 'Admin';
   const isTeacher = userRole === 'Teacher';

   const braineeResourcesItem = buildBraineeResourcesItem(
      subdomain,
      onUnavailable
   );

   const lmsItemsTeacher = braineeLMSMenuForTeacher(subdomain, onUnavailable);
   const lmsItemsCoTeacher = [
      getItem(link('/subject', 'Lesson'), '9', <ReadOutlined />),
      getItem(
         link('/formativeassessment/subjects', 'Formative Assessment'),
         '10',
         <FileDoneOutlined />
      ),
      getItem(
         link('/assessment/subjects', 'Summative Assessment'),
         '11',
         <FileDoneOutlined />
      ),
      getItem(
         link('/homeworksubjects', 'Homework & Project'),
         '12',
         <ProjectOutlined />
      ),
      getItem(
         link('/masterysubjects', 'Brainee Mastery Assessment'),
         '13',
         <FileDoneOutlined />
      ),
      getItem(
         link('#', 'Lesson Reports', onUnavailable),
         '14',
         <BarChartOutlined />
      ),
      getItem(
         link('/masteryassessmentreport', 'Mastery Assessment Reports'),
         '14.1',
         <BarChartOutlined />
      ),
   ];

   const eLib = eLibraryMenu(subdomain, onELibraryClick, 26);

   // 1TVET shows the old applicant list to every staff role except the ones that
   // reach the Registrar/Guidance menus above (App-Admin, Registrar, Interviewer,
   // Cashier, Clinic), gated on the department = -100 tag.
   const showOldApplicants =
      !['App-Admin', 'Registrar', 'Interviewer', 'Cashier', 'Clinic'].includes(userRole) &&
      hasOldApplicantAccess(userRole);

   const menu = [
      homeItem,
      showOldApplicants && oldApplicantsItem(),
      scheduleMenu,
      !isCoTeacher && reportCardMenu,
      attendanceMenu,
   ].filter(Boolean);

   // Brainee LMS menus
   if (!isCoTeacher && (isSuperAdmin || isAdmin || isTeacher)) {
      menu.push(
         getItem('Brainee LMS', 'sub3', <CloudOutlined />, lmsItemsTeacher)
      );
   }

   if (isCoTeacher) {
      menu.push(
         getItem('Brainee LMS', 'sub2', <CloudOutlined />, lmsItemsCoTeacher)
      );
   }

   // Unified Microcredentials menu (appears once)
   if (hasMicrocredentials) {
      const mcProps = { key: 'microcredentials' };

      mcProps.teacherGuideOnClick = onUnavailable;
      mcProps.lessonOnClick = onUnavailable;
      mcProps.homeworkTo = '/microcredentials/homeworks';
      mcProps.homeworkOnClick = clearSelectedTeacher;

      // Teacher/Admin roles get assessment and performance task access
      if (!isCoTeacher && (isSuperAdmin || isAdmin || isTeacher)) {
         mcProps.teacherGuideTo = '/microcredentials/teacher-guide';
         mcProps.teacherGuideOnClick = clearSelectedTeacher;
         mcProps.performanceTaskTo = '/microcredentials/performance-tasks';
         mcProps.performanceTaskOnClick = clearSelectedTeacher;
         mcProps.writtenWorksTo = '/microcredentials/written-works';
         mcProps.writtenWorksOnClick = clearSelectedTeacher;
         mcProps.assessmentTo = '/microcredsassessment/subjects';
         mcProps.assessmentOnClick = clearSelectedTeacher;
      }

      // Super admin also gets management link
      if (isSuperAdmin) {
         mcProps.managementTo = '/microcredentials-management';
         mcProps.managementOnClick = clearSelectedTeacher;
      }

      menu.push(microcredentialsItem(mcProps));
   }

   if (hasBraineeConference && (isSuperAdmin || isAdmin || isTeacher)) {
      menu.push(videoConferenceMenu(onLiveClassPickerOpen, onUnavailable));
   }

   if (!isSbu) {
      menu.push(braineeResourcesItem);
   }

   menu.push(eLib);

   if (!isCoTeacher) {
      menu.push(
         getItem(
            link('/notice', 'Notice Board'),
            '30',
            <NotificationOutlined />
         )
      );
   }

   if (isSuperAdmin) {
      menu.push(managementMenuAdmin);
   }

   if (isAdmin || isTeacher) {
      menu.push(studentInfoItem);
   }

   if (isSuperAdmin || isAdmin) {
      menu.push(
         getItem(
            link('/surveyassign', 'Survey Assignment'),
            '42',
            <AreaChartOutlined />
         )
      );
   }

   if (hasZoom && (isSuperAdmin || isAdmin || isTeacher)) {
      menu.push(zoomPickerItem(onZoomPickerOpen, ZoomIcon));
   }

   menu.push(logoutItem(onLogout));
   return menu;
}

// ---------------------------------------------------------------------------
// TVET menu — the 1TVET sidebar, reproduced entry for entry
// ---------------------------------------------------------------------------
//
// `../1TVET/1TVET-UI/src/components/side-menu/SideMenuNew.js` `GetMenuItems()` (:261-430) is the
// specification: which entries a TVET role sees, in which order, with which key, icon and wording.
// The CampusCloud builders above grew modules the legacy sidebar never had (mastery assessment,
// diagnostic, E-Library, lesson resources, video-conference sub-menu) and split Attendance into
// four entries, so a migrated TVET user was looking at a menu they had never seen. On a TVET
// tenant the sidebar is therefore built here instead. CampusCloud builders stay untouched — the
// branch is a single `isTvetTenant` test at the top of each builder.
//
// Config-gated modules stay visible: microcredentials, Brainee Video Conference, Zoom and the
// student's Brainee Live Class are appended when their `HAS_*` setting is on, even though the
// legacy sidebar had no equivalent, because switching them on is an explicit campus decision.
//
// Wording is kept verbatim where the legacy UI differed between roles: staff read "Unit of
// Competency Calendar" (:306) while students and parents read "Lesson Calendar" (:372, :400), and
// the microcredentialing heading keeps the legacy spelling (:331) because that is the string the
// campus saw.

const TVET_WADHWANI_COURSE_BASE =
   'https://skilling.wadhwanifoundation.org/en/courses/68239409c5896ba7c448f109/course-lesson';

// Legacy SideMenuNew.js:332-342 — eleven Wadhwani lessons behind one heading, makati only.
const TVET_MICROCREDENTIAL_COURSES = [
   { label: 'Introduction to Employability Skills ', path: '/4/94716' },
   { label: 'Oral Communication 1', path: '/12/94739' },
   { label: 'Reading and Writing 1', path: '/44/94737' },
   { label: 'Oral Communication 2', path: '/60/94873' },
   { label: 'Personality Development 1', path: '/76/94886' },
   { label: 'Personality Development 2', path: '/115/94757' },
   { label: 'Mathematics', path: '/161/94811' },
   { label: 'Professionalism', path: '/193/94838' },
   { label: 'Customer Centricity', path: '/238/94723' },
   { label: 'Empowerment Technology 1', path: '/266/94813' },
   { label: 'Empowerment Technology 2', path: '/305/94790' },
];

const openInNewTab = (url) => () => window.open(url, '_blank');

// Extracted from buildStaffMenu so the TVET builder offers the identical module.
const videoConferenceMenu = (onLiveClassPickerOpen, onUnavailable) =>
   getItem('Brainee Video Conference', 'sub6', <VideoCameraOutlined />, [
      getItem(
         link('#', 'Brainee Live Class Picker', onLiveClassPickerOpen),
         '19.1',
         <VideoCameraOutlined />
      ),
      getItem(link('#', 'Brainee Meeting Room', onUnavailable), '19.2', <VideoCameraOutlined />),
   ]);

const zoomPickerItem = (onZoomPickerOpen, ZoomIcon) =>
   getItem(link('#', 'Zoom Picker', onZoomPickerOpen), '44', <ZoomIcon />);

const tvetMicrocredentialingMenu = (subdomain) => {
   // Legacy gated this on `subdomain === 'makati' || 'localhost'` (:330); 'localhost' is not a
   // TVET campus, so only makati keeps the module.
   if (subdomain !== 'makati') {
      return null;
   }

   return getItem(
      'Microcrendentialing',
      'sub3',
      <CloudOutlined />,
      TVET_MICROCREDENTIAL_COURSES.map((course, index) =>
         getItem(
            link('#', course.label, openInNewTab(`${TVET_WADHWANI_COURSE_BASE}${course.path}`)),
            `${50 + index}`,
            <ProjectOutlined />
         )
      )
   );
};

// Legacy staff drew Class Schedule with the schedule icon and students/parents with the calendar
// one; the default keeps the student/parent look.
const tvetScheduleMenu = (calendarLabel, classScheduleIcon = <CalendarOutlined />) =>
   getItem('Schedule', 'sub0', <ScheduleOutlined />, [
      getItem(link('/classsched', 'Class Schedule'), '2', classScheduleIcon),
      getItem(link('/lessoncalendar', calendarLabel), '3', <CalendarOutlined />),
      getItem(link('/schoolcalendar', 'School Calendar'), '4', <CalendarOutlined />),
   ]);

// Legacy staff/parent report card points at the staff module, students at their own (:312, :376).
const tvetStaffReportCardMenu = getItem(
   'Report Card & Certificates',
   'sub1',
   <ProfileOutlined />,
   [
      getItem(link('/reportcards', 'Report Card'), '5', <ProfileOutlined />),
      getItem(link('/certificates', 'Certificate'), '6', <CalendarOutlined />),
   ]
);

const tvetStudentReportCardMenu = getItem(
   'Report Card & Certificates',
   'sub1',
   <ProfileOutlined />,
   [
      getItem(link('/studentreportcard', 'Report Card'), '5', <CalendarOutlined />),
      getItem(link('/studentcertificate', 'Certificate'), '6', <CalendarOutlined />),
   ]
);

// One Attendance entry, not the Classroom/Period/Gate/Report sub-menu (:317, :380).
const tvetAttendanceItem = getItem(link('/attendance', 'Attendance'), '8', <CarryOutOutlined />);

const tvetNoticeBoardItem = getItem(link('/notice', 'Notice Board'), '30', <NotificationOutlined />);

const tvetSurveyAssignmentItem = getItem(
   link('/surveyassign', 'Survey Assignment'),
   '38',
   <AreaChartOutlined />
);

// Students answered a survey (:423) — the legacy wording is kept.
const tvetSurveyItem = getItem(link('/survey', 'Survey'), '39', <AreaChartOutlined />);

const tvetStaffLmsMenu = getItem('Brainee LMS', 'sub2', <CloudOutlined />, [
   getItem(link('/subject', TVET_LABELS.lesson, clearSelectedTeacher), '9', <ReadOutlined />),
   getItem(
      link('/formativeassessment/subjects', TVET_LABELS.formativeAssessment, clearSelectedTeacher),
      '10',
      <FileDoneOutlined />
   ),
   getItem(
      link('/assessment/subjects', TVET_LABELS.summativeAssessment, clearSelectedTeacher),
      '11',
      <FileDoneOutlined />
   ),
   getItem(link('/homeworksubjects', TVET_LABELS.homework), '12', <ProjectOutlined />),
   virtualSimulationItem('13'),
]);

const tvetStudentLmsMenu = getItem('Brainee LMS', 'sub2', <CloudOutlined />, [
   // Legacy student wording is the short "Unit" (:383), not the staff label.
   getItem(link('/subject', 'Unit'), '7', <ReadOutlined />),
   getItem(
      link('/studentformativeassessment/subjects', TVET_LABELS.formativeAssessment),
      '10',
      <FileDoneOutlined />
   ),
   getItem(
      link('/studentassessment/subjects', TVET_LABELS.summativeAssessment),
      '11',
      <FileDoneOutlined />
   ),
   getItem(
      link('/studenthomeworks/subjects', TVET_LABELS.homework),
      '15',
      <ProjectOutlined />
   ),
   virtualSimulationItem('12'),
]);

// Super Admin / Admin Management sub-menu (:350-360). The legacy UI commented out the two
// application-form builders because their tables do not exist on TVET databases, which is the same
// conclusion TvetTenant.js reached independently — and the same reason it blocks /appinfogroups and
// /appinfofields (see UNAVAILABLE_FEATURES).
//
// Configuration Settings is the exception: the legacy menu predates the page, so it was never listed,
// but /configsettings is a live route in App.js and ConfigSettings.js itself admits exactly the two
// roles that reach this sub-menu (super admin + admin). TvetTenant.js does not block it, so the only
// thing keeping it out of the sidebar was this list.
const tvetManagementMenuAdmin = getItem('Management', 'sub5', <ControlOutlined />, [
   getItem(link('/managestudentinfo', 'Student Information'), '31', <SolutionOutlined />),
   getItem(link('/promotestudents', 'Promote Students'), '32', <ToTopOutlined />),
   getItem(link('/subjects', 'Subjects'), '33', <EditOutlined />),
   getItem(link('/subjectgroups', 'Subject Groups'), '34', <ClusterOutlined />),
   getItem(link('/sections', 'Sections'), '35', <UnorderedListOutlined />),
   getItem(link('/levels', 'Levels'), '36', <OrderedListOutlined />),
   getItem(link('/managestaffinfo', 'Staff Information'), '37', <TeamOutlined />),
   getItem(
      link('/configsettings', 'Configuration Settings'),
      '40',
      <SettingOutlined />
   ),
]);

// Teachers only ever got Student Information (:363-367).
const tvetManagementMenuTeacher = getItem('Management', 'sub5', <ControlOutlined />, [
   getItem(link('/managestudentinfo', 'Student Information'), '31', <IdcardOutlined />),
]);

const braineeLiveClassItem = (onLiveClassJoin) =>
   getItem(link('#', 'Brainee Live Class', onLiveClassJoin), '50', <VideoCameraOutlined />);

function tvetStaffMenu({
   userRole,
   subdomain,
   hasBraineeConference,
   hasMicrocredentials,
   hasZoom,
   ZoomIcon,
   onUnavailable,
   onLiveClassPickerOpen,
   onZoomPickerOpen,
   onLogout,
}) {
   const isSuperAdmin = userRole === 'Super Admin';
   const isAdmin = userRole === 'Admin';
   const isTeacher = userRole === 'Teacher';

   // Order follows the legacy builder: Home → applicants → Schedule → Report Card → Attendance →
   // Brainee LMS → microcredentialing → Notice Board → Management → Survey Assignment → Logout.
   const menu = [homeItem];

   if (hasOldApplicantAccess(userRole)) {
      menu.push(oldApplicantsItem());
   }

   menu.push(tvetScheduleMenu(`${TVET_LABELS.lesson} Calendar`, <ScheduleOutlined />));
   menu.push(tvetStaffReportCardMenu);
   menu.push(tvetAttendanceItem);
   menu.push(tvetStaffLmsMenu);
   menu.push(tvetMicrocredentialingMenu(subdomain));

   if (isSuperAdmin || isAdmin || isTeacher) {
      menu.push(tvetNoticeBoardItem);
   }

   if (isSuperAdmin || isAdmin) {
      menu.push(tvetManagementMenuAdmin);
   }

   if (isTeacher) {
      menu.push(tvetManagementMenuTeacher);
   }

   if (isSuperAdmin || isAdmin) {
      menu.push(tvetSurveyAssignmentItem);
   }

   if (hasMicrocredentials) {
      const mcProps = { key: 'microcredentials' };

      mcProps.lessonOnClick = onUnavailable;
      mcProps.homeworkTo = '/microcredentials/homeworks';
      mcProps.homeworkOnClick = clearSelectedTeacher;

      if (isSuperAdmin || isAdmin || isTeacher) {
         mcProps.teacherGuideTo = '/microcredentials/teacher-guide';
         mcProps.teacherGuideOnClick = clearSelectedTeacher;
         mcProps.performanceTaskTo = '/microcredentials/performance-tasks';
         mcProps.performanceTaskOnClick = clearSelectedTeacher;
         mcProps.writtenWorksTo = '/microcredentials/written-works';
         mcProps.writtenWorksOnClick = clearSelectedTeacher;
         mcProps.assessmentTo = '/microcredsassessment/subjects';
         mcProps.assessmentOnClick = clearSelectedTeacher;
      }

      if (isSuperAdmin) {
         mcProps.managementTo = '/microcredentials-management';
         mcProps.managementOnClick = clearSelectedTeacher;
      }

      menu.push(microcredentialsItem(mcProps));
   }

   if (hasBraineeConference && (isSuperAdmin || isAdmin || isTeacher)) {
      menu.push(videoConferenceMenu(onLiveClassPickerOpen, onUnavailable));
   }

   if (hasZoom && (isSuperAdmin || isAdmin || isTeacher)) {
      menu.push(zoomPickerItem(onZoomPickerOpen, ZoomIcon));
   }

   menu.push(logoutItem(onLogout));

   return menu.filter(Boolean);
}

function tvetStudentMenu({
   subdomain,
   userRole,
   hasEmergencySheet,
   hasBraineeConference,
   onLiveClassJoin,
   onLogout,
}) {
   // Legacy order for a Student (:297-301, :330, :369-394, :422): the microcredentialing block and
   // the applicant list were pushed before the student-specific block, so they appear first.
   const menu = [homeItem];

   if (hasOldApplicantAccess(userRole)) {
      menu.push(oldApplicantsItem());
   }

   menu.push(tvetMicrocredentialingMenu(subdomain));
   menu.push(tvetScheduleMenu('Lesson Calendar'));
   menu.push(tvetStudentReportCardMenu);
   menu.push(tvetAttendanceItem);
   menu.push(tvetStudentLmsMenu);

   if (hasEmergencySheet) {
      menu.push(
         getItem(
            link('/studentemergencyinfo', 'Emergency Information'),
            '12.1',
            <ProfileOutlined />
         )
      );
   }

   if (hasBraineeConference) {
      menu.push(braineeLiveClassItem(onLiveClassJoin));
   }

   menu.push(tvetSurveyItem);
   menu.push(logoutItem(onLogout));

   return menu.filter(Boolean);
}

function tvetParentMenu({ userRole, onUnavailable, onLogout }) {
   // Legacy parents (:297-301, :397-415): Home and the applicant list first, then Schedule,
   // Report Card, Attendance, homework and the Student Records block — no enrollment item.
   const menu = [homeItem];

   if (hasOldApplicantAccess(userRole)) {
      menu.push(oldApplicantsItem());
   }

   menu.push(tvetScheduleMenu('Lesson Calendar'));
   menu.push(tvetStudentReportCardMenu);
   menu.push(tvetAttendanceItem);
   menu.push(getItem(link('/studenthomeworks', 'Homework & Projects'), '9', <ProjectOutlined />));
   menu.push(
      getItem('Student Records', 'sub2', <AccountBookOutlined />, [
         getItem(link('#', 'Student Information', onUnavailable), '10', <ProfileOutlined />),
         getItem(link('#', 'Statement of Accounts', onUnavailable), '11', <AccountBookOutlined />),
         getItem(link('#', 'Payment History', onUnavailable), '12', <HistoryOutlined />),
      ])
   );
   menu.push(logoutItem(onLogout));

   return menu.filter(Boolean);
}

function tvetApplicantListMenu({ userRole, subdomain, onLogout }) {
   // Interviewer / Guidance / Cashier / Clinic only ever saw the applicant list (:291-295);
   // Registrar and App-Admin also saw the old applicant list and, outside makati, Examination.
   const menu = [getItem(link('/applicantlist', 'Student Applicants'), '1', <TeamOutlined />)];

   if (['Interviewer', 'Guidance', 'Cashier', 'Clinic'].includes(userRole)) {
      menu.push(logoutItem(onLogout));
      return menu;
   }

   if (hasOldApplicantAccess(userRole)) {
      menu.push(oldApplicantsItem());
   }

   if (subdomain !== 'makati') {
      menu.push(
         getItem(link('/assessment/subjects', 'Examination (LMS)'), '2', <FileDoneOutlined />)
      );
   }

   menu.push(logoutItem(onLogout));

   return menu;
}

function tvetEnrollmentStudentMenu(subdomain, onLogout) {
   // Legacy status-'0' students saw Upload on makati and Examination everywhere else (:264-273).
   return [
      subdomain === 'makati'
         ? getItem(link('/upload-docs', 'Upload'), '2', <CloudUploadOutlined />)
         : getItem(
              link('/studentassessment/subjects', 'Examination'),
              '11',
              <FileDoneOutlined />
           ),
      logoutItem(onLogout),
   ];
}

// ---------------------------------------------------------------------------
// Brainee Resources (access-controlled)
// ---------------------------------------------------------------------------

export function buildBraineeResourcesItem(subdomain, onUnavailable) {
   const isAllowed = Utils.hasBraineeResourcesAccess(subdomain);
   return getItem(
      link(
         isAllowed ? '/brainee-resources' : '#',
         'Brainee Resources',
         isAllowed ? undefined : onUnavailable
      ),
      '41',
      <DatabaseOutlined />
   );
}

// ---------------------------------------------------------------------------
// Sudeco special case
// ---------------------------------------------------------------------------

export function buildSudecoMenu(userRole, onLogout) {
   if (['Registrar', 'Guidance', 'Cashier'].includes(userRole)) return [];

   const menu = [homeItem];

   if (['Super Admin', 'Admin', 'Teacher'].includes(userRole)) {
      menu.push(
         getItem(
            link('/classsched', 'Class Schedule'),
            '2',
            <ScheduleOutlined />
         ),
         getItem(link('/reportcards', 'Report Card'), '5', <ProfileOutlined />)
      );
   }

   menu.push(logoutItem(onLogout));
   return menu;
}




